/**
 * Thin React wrapper around `@novnc/novnc`'s `RFB` class. Owns the
 * canvas mount, the WS lifecycle, and the ticket-refresh loop that
 * swaps the short-TTL `?token=` ~10 s before it expires (handoff §7.2).
 *
 * Design notes:
 *   - RFB drops the WS on its own when the server disconnects. We
 *     listen for `disconnect` events and attempt a single transparent
 *     reconnect with the freshest cached ticket; further drops surface
 *     to the parent via `onDisconnect`.
 *   - The token NEVER lands in localStorage, the page URL, an error
 *     toast, console.log, or any other surface (handoff §7.4 hard
 *     rule). It lives in a ref and the WS URL passed to RFB.
 *   - The mount div has `tabIndex={0}` so the user can focus + type
 *     into the Chromium without click-first.
 *   - Resize: viewBox-style scaling via `scaleViewport: true`.
 */

import { useEffect, useRef, useState } from "react";
// @ts-expect-error — @novnc/novnc ships ESM with no bundled .d.ts.
// The RFB constructor signature we use is stable since v1.4: the lib's
// own /docs/API.md is the canonical reference, but the call shape is
// `new RFB(target, url, options?)` with a `disconnect()` cleanup.
import RFB from "@novnc/novnc";
import { getVncTicket, type VncTicket } from "@/api/lx-openclaw-config";
import { getToken } from "@/lib/lexiun-jwt";
import { buildVncWsUrl } from "./vnc-url";

export type NoVncStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export interface NoVncPaneProps {
  /** The lexserver-tunneled, `userId`-prefixed WS path from
   *  `getBrowserStatus()` (e.g.
   *  `/ws/runtime/<userId>/api/lx/openclaw/browser/vnc/ws`). The component
   *  joins this with the lexserver WS origin (prod, via
   *  `VITE_LEXSERVER_WS_URL`) — or the page origin in dev (Vite proxy) —
   *  plus a fresh `?token=`. See `./vnc-url`. */
  vncEndpoint: string;
  /** Called whenever the visible status flips so the parent can
   *  render its "Live view active" indicator etc. */
  onStatusChange?: (status: NoVncStatus, detail?: string) => void;
  className?: string;
}

export function NoVncPane({
  vncEndpoint,
  onStatusChange,
  className,
}: NoVncPaneProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  // Hold the RFB instance + current ticket in refs so the effect that
  // sets the refresh interval doesn't restart on parent re-renders.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rfbRef = useRef<any>(null);
  const ticketRef = useRef<VncTicket | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<NoVncStatus>("idle");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  // Mirror `status` into a ref so the 30 s connect-timeout closure can
  // read the freshest value without becoming a deps-tracking footgun.
  // Per P4.D.UI of openclaw-settings-p4d-novnc-plan.md §12.
  const statusRef = useRef<NoVncStatus>("idle");

  // Mirror status updates to the parent callback (kept in a ref so the
  // effect doesn't depend on a possibly-unmemoized handler).
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);
  function setStatusAndNotify(next: NoVncStatus, detail?: string) {
    statusRef.current = next;
    setStatus(next);
    setErrorDetail(detail ?? null);
    onStatusChangeRef.current?.(next, detail);
  }

  useEffect(() => {
    let cancelled = false;
    const mount = mountRef.current;
    if (!mount) return;

    // 30 s connect-timeout (P4.D.UI §12). RFB only emits `connect` once
    // the full RFB handshake completes; if the underlying WS never
    // upgrades (e.g. websockify rejected the token, the runtime is
    // unreachable, or the lexserver `/ws` tunnel is wedged), the user
    // would otherwise stare at a frozen "connecting" forever. After
    // 30 s with no `connect` and no `disconnect`, flip to `error` so
    // the parent's ActorStrip surfaces the failure.
    let connectTimer: ReturnType<typeof setTimeout> | null = null;

    async function start() {
      if (cancelled) return;
      setStatusAndNotify("connecting");
      let ticket: VncTicket;
      try {
        ticket = await getVncTicket();
      } catch (err) {
        if (cancelled) return;
        setStatusAndNotify(
          "error",
          err instanceof Error ? err.message : String(err),
        );
        return;
      }
      if (cancelled) return;
      ticketRef.current = ticket;
      const wsUrl = buildVncWsUrl(vncEndpoint, ticket.token);
      // P4.D.W: offer the Lexiun JWT as a WebSocket subprotocol so lexserver's
      // runtime-ws-proxy VNC branch (parseUpgradeAuth) accepts the tunneled
      // upgrade — websockify validates the `?token=` separately. The direct
      // host-port debug fallback ignores the JWT subprotocol; `"binary"` is
      // offered too for it / noVNC. See ai-docs/lexruntime-noVNC-monkey-patch.md.
      const lexiunJwt = await getToken();
      if (cancelled) return;
      try {
        const rfb = new RFB(mount, wsUrl, {
          wsProtocols: [`lexiun-jwt.${lexiunJwt}`, "binary"],
          // No shared-secret repeater, no credentials — websockify
          // validates the `?token=` server-side. RFB's `credentials`
          // would only be read for VNC's native auth schemes.
        });
        rfb.scaleViewport = true;
        rfb.resizeSession = false;
        rfb.background = "transparent";
        rfb.addEventListener("connect", () => {
          if (cancelled) return;
          setStatusAndNotify("connected");
          scheduleRefresh(ticket.ttlMs);
        });
        rfb.addEventListener("disconnect", (ev: { detail?: { clean?: boolean } }) => {
          if (cancelled) return;
          // RFB fires `disconnect` on both clean teardown and server
          // drops. Surface the status; the AgentBrowserLayout actor
          // strip then shows "disconnected" — the user reconnects by
          // clicking Close → Open on the profile picker, which
          // re-triggers `launchBrowserProfile` and (because the
          // pane's effect dep changes on subsequent vncEndpoint
          // probes) remounts the RFB session cleanly.
          setStatusAndNotify(
            ev?.detail?.clean ? "disconnected" : "error",
            ev?.detail?.clean ? undefined : "websocket-closed",
          );
          clearRefresh();
        });
        // RFB security-negotiation events. `securityfailure` fires when
        // the server's auth handshake rejects (e.g. websockify's
        // TokenFile plugin saw a stale token); `credentialsrequired`
        // fires when the server demands VNC-native credentials we
        // never wire (we rely entirely on websockify's `?token=` —
        // seeing this event means the runtime config drifted).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rfb.addEventListener("securityfailure", (e: any) => {
          if (cancelled) return;
          setStatusAndNotify(
            "error",
            `security-failure:${e?.detail?.status ?? "?"}`,
          );
        });
        rfb.addEventListener("credentialsrequired", () => {
          if (cancelled) return;
          setStatusAndNotify("error", "credentials-required");
        });
        rfbRef.current = rfb;
        connectTimer = setTimeout(() => {
          if (cancelled) return;
          if (statusRef.current === "connecting") {
            setStatusAndNotify("error", "connect-timeout");
            try {
              rfb.disconnect();
            } catch {
              /* ignore — RFB may not yet have a WS to close */
            }
          }
        }, 30_000);
      } catch (err) {
        if (cancelled) return;
        setStatusAndNotify(
          "error",
          err instanceof Error ? err.message : String(err),
        );
      }
    }

    void start();

    return () => {
      cancelled = true;
      clearRefresh();
      if (connectTimer !== null) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }
      // RFB.disconnect() closes the WS + tears down event listeners.
      // Guarded so a re-mount during connect() doesn't throw.
      try {
        rfbRef.current?.disconnect();
      } catch {
        /* RFB raises if not yet connected — tolerate. */
      }
      rfbRef.current = null;
      ticketRef.current = null;
    };
    // vncEndpoint changes on profile switch — restart cleanly.
  }, [vncEndpoint]);

  function clearRefresh() {
    if (refreshTimerRef.current !== null) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }

  /** Mint a fresh ticket every `ttlMs - 10 s` and stash it for the
   *  next reconnect. RFB itself stays connected as long as websockify
   *  doesn't drop us — the refreshed token becomes useful on the next
   *  reconnect (mid-session ticket replacement isn't an upstream API). */
  function scheduleRefresh(ttlMs: number) {
    clearRefresh();
    const intervalMs = Math.max(15_000, ttlMs - 10_000);
    refreshTimerRef.current = window.setInterval(async () => {
      try {
        const next = await getVncTicket();
        ticketRef.current = next;
      } catch {
        // Soft-fail: keep the existing (still valid) ticket until
        // its expiry. The next RFB disconnect surfaces to the user
        // via the status indicator + Reconnect button.
      }
    }, intervalMs);
  }

  return (
    <div
      data-testid="agent-browser-vnc-pane"
      data-status={status}
      data-error-detail={errorDetail ?? undefined}
      className={className}
    >
      <div
        ref={mountRef}
        tabIndex={0}
        className="h-full w-full focus:outline-none"
      />
    </div>
  );
}
